import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { ClockIcon, ExclamationTriangleIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import { Conversation } from 'src/services/whatsapp'
import { billingQueries, isWalletBalanceLow } from 'src/services/billing'
import { isInsufficientBalanceError } from 'src/utils/errors'
import { InlineHelp } from '../inline-help'
import { MessageInput } from './message-input'
import { SendPayload, TemplateComposer } from './template-composer'

interface MessageComposerProps {
  conversation: Conversation
  onSend: (payload: SendPayload) => void
  isLoading?: boolean
  sendError?: Error | null
}

/** How often the countdown is recomputed so the composer flips to template mode
 * the moment the window lapses, rather than on the next conversation poll. */
const TICK_MS = 30_000

/** Remaining window time split for a coarse "23h 12m" / "12m" label. */
function splitRemaining(ms: number): { hours: number; minutes: number } {
  const totalMinutes = Math.max(0, Math.floor(ms / 60_000))
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 }
}

/**
 * The composer, shaped by WhatsApp's 24-hour customer-service window so a send
 * is never composed only to be rejected by the API.
 *
 * Inside the window the team writes freely; outside it — including first
 * contact, which has no inbound history at all — only an approved template may
 * be sent, so the free-form box is not rendered. The window state is derived
 * from the server's `isWindowOpen` plus a client-side expiry check, so a window
 * lapsing while the page is open closes the free-form box immediately.
 */
export const MessageComposer = ({
  conversation,
  onSend,
  isLoading = false,
  sendError = null,
}: MessageComposerProps) => {
  const { t } = useTranslation()
  const [, setTick] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => setTick((n) => n + 1), TICK_MS)
    return () => clearInterval(interval)
  }, [])

  // The raw send failure is logged for debugging but never shown — the user
  // reads a plain message (or the balance-specific cue), not an API/provider string.
  useEffect(() => {
    if (sendError) console.error('WhatsApp send failed', sendError)
  }, [sendError])

  const remainingMs = conversation.windowExpiresAt
    ? new Date(conversation.windowExpiresAt).getTime() - Date.now()
    : 0
  const isWindowOpen = Boolean(conversation.isWindowOpen) && remainingMs > 0

  // A best-effort read of the billed org's wallet so the team sees a low-balance
  // warning *before* a send is rejected. Non-blocking: if it can't be read (no
  // org, no permission), no warning shows and sends still proceed. A top-up
  // invalidates this query, so the warning clears the moment funds arrive.
  const { data: wallet } = useQuery(billingQueries.wallet(conversation.organization))
  const isBalanceLow = isWalletBalanceLow(wallet)

  const { hours, minutes } = splitRemaining(remainingMs)
  const remaining =
    hours < 1 && minutes < 1
      ? t('chat.remainingUnderAMinute')
      : hours < 1
      ? t('chat.remainingMinutes', { minutes })
      : t('chat.remainingHoursMinutes', { hours, minutes })

  return (
    <div className="border-t border-border bg-surface">
      <div
        className={
          isWindowOpen
            ? 'flex items-center gap-2 border-b border-green-200 bg-green-50 px-4 py-2 text-xs text-green-700'
            : 'flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border bg-surface-hover px-4 py-2 text-xs text-text-secondary'
        }
        data-testid="composer-window-state"
      >
        {isWindowOpen ? (
          <>
            <ClockIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{t('chat.windowOpen', { remaining })}</span>
          </>
        ) : (
          <>
            <LockClosedIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="font-medium">{t('chat.windowClosedTemplateRequired')}</span>
            <span>{t('chat.windowClosedHint')}</span>
          </>
        )}
      </div>

      <div className="px-4 pt-2">
        <InlineHelp
          detail={t(isWindowOpen ? 'help.window24hDetail' : 'help.templateRequirementDetail')}
          data-testid={isWindowOpen ? 'help-window-24h' : 'help-template-requirement'}
        />
      </div>

      {isBalanceLow && (
        <div
          className="border-warning/40 bg-warning/10 mx-4 mt-3 flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-text-primary"
          data-testid="composer-low-balance-warning"
        >
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <span>{t('billing.lowBalanceWarning')}</span>
          <Link to="/billing" className="ml-auto font-medium underline hover:no-underline">
            {t('billing.topUp')}
          </Link>
        </div>
      )}

      {sendError && (
        <div className="mx-4 mt-3 flex items-center gap-2 rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-800">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {isInsufficientBalanceError(sendError) ? (
            <>
              <span>{t('billing.insufficientBalance')}</span>
              <Link to="/billing" className="ml-auto font-medium underline hover:no-underline">
                {t('billing.topUp')}
              </Link>
            </>
          ) : (
            <span>{t('chat.failedToSend')}</span>
          )}
        </div>
      )}

      {isWindowOpen ? (
        <MessageInput
          onSend={(content, mediaUrls) => onSend({ content, mediaUrls })}
          isLoading={isLoading}
        />
      ) : (
        <TemplateComposer conversation={conversation} onSend={onSend} isLoading={isLoading} />
      )}
    </div>
  )
}
